/**
 * Enterprise Features API Service
 * Connects React frontend to PHP enterprise engines
 */

const API_BASE = '/enterprise-api';

interface ApiResponse<T = any> {
  success?: boolean;
  error?: string;
  data?: T;
  [key: string]: any;
}

async function apiCall<T = any>(
  module: string,
  action: string,
  options: {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    id?: number | string;
    body?: any;
  } = {}
): Promise<T> {
  const { method = 'GET', id, body } = options;
  
  let url = `${API_BASE}?module=${module}&action=${action}`;
  if (id) url += `&id=${id}`;
  
  const fetchOptions: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
    },
  };
  
  if (body && method !== 'GET') {
    fetchOptions.body = JSON.stringify(body);
  }
  
  const response = await fetch(url, fetchOptions);
  const data = await response.json();
  
  if (data.error) {
    throw new Error(data.error);
  }
  
  return data;
}

// ═══════════════════════════════════════════════════════════════════════════
// VENDOR RISK
// ═══════════════════════════════════════════════════════════════════════════

export const vendorRiskService = {
  getSummary: () => apiCall('vendor-risk', 'summary'),
  getVendor: (id: number) => apiCall('vendor-risk', 'vendor', { id }),
  getQuestionnaires: () => apiCall('vendor-risk', 'questionnaires'),
  calculateRisk: (supplierId: number, riskFactors: any) => 
    apiCall('vendor-risk', 'calculate-risk', { method: 'POST', body: { supplier_id: supplierId, risk_factors: riskFactors } }),
  sendQuestionnaire: (supplierId: number, templateId?: number) =>
    apiCall('vendor-risk', 'send-questionnaire', { method: 'POST', body: { supplier_id: supplierId, template_id: templateId } }),
};

// ═══════════════════════════════════════════════════════════════════════════
// TRAINING
// ═══════════════════════════════════════════════════════════════════════════

export const trainingService = {
  getCourses: () => apiCall('training', 'courses'),
  getCourse: (id: number) => apiCall('training', 'course', { id }),
  getAssignments: (userId?: number) => apiCall('training', 'assignments', { id: userId }),
  getComplianceSummary: () => apiCall('training', 'compliance-summary'),
  getOverdue: () => apiCall('training', 'overdue'),
  assignCourse: (courseId: number, userIds: number[], dueDays?: number) =>
    apiCall('training', 'assign', { method: 'POST', body: { course_id: courseId, user_ids: userIds, due_days: dueDays } }),
  completeCourse: (assignmentId: number, userId: number, answers?: any) =>
    apiCall('training', 'complete', { method: 'POST', body: { assignment_id: assignmentId, user_id: userId, answers } }),
  createCourse: (data: any) => apiCall('training', 'create', { method: 'POST', body: data }),
};

// ═══════════════════════════════════════════════════════════════════════════
// AUTOMATION RULES
// ═══════════════════════════════════════════════════════════════════════════

export const automationService = {
  getRules: () => apiCall('automation', 'rules'),
  getRule: (id: number) => apiCall('automation', 'rule', { id }),
  getExecutionStats: () => apiCall('automation', 'execution-stats'),
  getAvailableTriggers: () => apiCall('automation', 'triggers'),
  getAvailableActions: () => apiCall('automation', 'actions'),
  createRule: (data: any) => apiCall('automation', 'create', { method: 'POST', body: data }),
  updateRule: (id: number, data: any) => apiCall('automation', 'update', { method: 'PUT', id, body: data }),
  deleteRule: (id: number) => apiCall('automation', 'delete', { method: 'DELETE', id }),
  fireTrigger: (triggerType: string, context: any) =>
    apiCall('automation', 'fire', { method: 'POST', body: { trigger_type: triggerType, context } }),
};

// ═══════════════════════════════════════════════════════════════════════════
// CLOUD SECURITY
// ═══════════════════════════════════════════════════════════════════════════

export const cloudService = {
  getIntegrations: () => apiCall('cloud', 'integrations'),
  getIntegration: (id: number) => apiCall('cloud', 'integration', { id }),
  getFindings: (integrationId?: number) => apiCall('cloud', 'findings', { id: integrationId }),
  getFindingsSummary: () => apiCall('cloud', 'summary'),
  syncIntegration: (id: number) => apiCall('cloud', 'sync', { method: 'POST', id }),
  createIntegration: (data: any) => apiCall('cloud', 'create', { method: 'POST', body: data }),
  updateIntegration: (id: number, data: any) => apiCall('cloud', 'update', { method: 'PUT', id, body: data }),
  deleteIntegration: (id: number) => apiCall('cloud', 'delete', { method: 'DELETE', id }),
};

// ═══════════════════════════════════════════════════════════════════════════
// SAAS INTEGRATIONS
// ═══════════════════════════════════════════════════════════════════════════

export const saasService = {
  getIntegrations: () => apiCall('saas', 'integrations'),
  getIntegration: (id: number) => apiCall('saas', 'integration', { id }),
  getComplianceSummary: () => apiCall('saas', 'compliance-summary'),
  testConnection: (id: number) => apiCall('saas', 'test', { method: 'POST', id }),
  syncIntegration: (id: number) => apiCall('saas', 'sync', { method: 'POST', id }),
  createIntegration: (data: any) => apiCall('saas', 'create', { method: 'POST', body: data }),
  updateIntegration: (id: number, data: any) => apiCall('saas', 'update', { method: 'PUT', id, body: data }),
  deleteIntegration: (id: number) => apiCall('saas', 'delete', { method: 'DELETE', id }),
};

// ═══════════════════════════════════════════════════════════════════════════
// ENDPOINT AGENTS
// ═══════════════════════════════════════════════════════════════════════════

export const endpointService = {
  getEndpoints: () => apiCall('endpoints', 'list'),
  getEndpoint: (id: number) => apiCall('endpoints', 'endpoint', { id }),
  getFleetSummary: () => apiCall('endpoints', 'fleet-summary'),
  registerAgent: (data: any) => apiCall('endpoints', 'register', { method: 'POST', body: data }),
  recordCheckResults: (agentId: string, results: any) =>
    apiCall('endpoints', 'check-results', { method: 'POST', body: { agent_id: agentId, results } }),
  deleteEndpoint: (id: number) => apiCall('endpoints', 'delete', { method: 'DELETE', id }),
};

// ═══════════════════════════════════════════════════════════════════════════
// PENTEST INTEGRATION
// ═══════════════════════════════════════════════════════════════════════════

export const pentestService = {
  getReports: () => apiCall('pentest', 'reports'),
  getReport: (id: number) => apiCall('pentest', 'report', { id }),
  getFindings: (reportId?: number) => apiCall('pentest', 'findings', { id: reportId }),
  getFinding: (id: number) => apiCall('pentest', 'finding', { id }),
  getSupportedTools: () => apiCall('pentest', 'tools'),
  getRemediationStatus: (reportId: number) => apiCall('pentest', 'remediation-status', { id: reportId }),
  importReport: (tool: string, filePath: string, metadata?: any) =>
    apiCall('pentest', 'import', { method: 'POST', body: { tool, file_path: filePath, metadata } }),
  updateFindingStatus: (findingId: number, status: string, data?: any) =>
    apiCall('pentest', 'update-finding', { method: 'PUT', id: findingId, body: { status, ...data } }),
  createReport: (data: any) => apiCall('pentest', 'create', { method: 'POST', body: data }),
  bulkUpdateFindings: (findingIds: number[], data: any) =>
    apiCall('pentest', 'bulk-update', { method: 'POST', body: { finding_ids: findingIds, data } }),
  linkFindingsToControl: (findingIds: number[], controlId: number) =>
    apiCall('pentest', 'link-controls', { method: 'POST', body: { finding_ids: findingIds, control_id: controlId } }),
  updateFinding: (findingId: number, data: any) =>
    apiCall('pentest', 'update-finding', { method: 'PUT', id: findingId, body: data }),
  reprocessReport: (reportId: number) =>
    apiCall('pentest', 'reprocess', { method: 'POST', id: reportId }),
  getComments: (findingId: number) =>
    apiCall('pentest', 'comments', { id: findingId }),
  addComment: (findingId: number, content: string, author?: string) =>
    apiCall('pentest', 'comments', { method: 'POST', id: findingId, body: { finding_id: findingId, content, author: author || 'User' } }),
  getAISuggestion: (findingId: number) =>
    apiCall('pentest', 'ai-suggest', { method: 'POST', id: findingId }),
  deleteReport: (reportId: number) =>
    apiCall('pentest', 'delete-report', { method: 'DELETE', id: reportId }),
  getReportImages: (reportId: number) =>
    apiCall('pentest', 'report-images', { id: reportId }),
  uploadEvidenceImage: async (findingId: number, image: File, analyzeWithAI: boolean = false) => {
    const formData = new FormData();
    formData.append('image', image);
    formData.append('analyze_with_ai', analyzeWithAI ? '1' : '0');
    const url = `${API_BASE}?module=pentest&action=upload-evidence&id=${findingId}`;
    const response = await fetch(url, { method: 'POST', body: formData });
    const result = await response.json();
    if (result.error) throw new Error(result.error);
    return result;
  },
  uploadReport: async (data: { title: string; vendor_name: string; test_type: string; scope_description: string; file: File | null }) => {
    const formData = new FormData();
    formData.append('title', data.title);
    formData.append('vendor_name', data.vendor_name);
    formData.append('test_type', data.test_type);
    formData.append('scope_description', data.scope_description);
    if (data.file) {
      formData.append('file', data.file);
    }
    const url = `${API_BASE}?module=pentest&action=upload`;
    const response = await fetch(url, { method: 'POST', body: formData });
    const result = await response.json();
    if (result.error) throw new Error(result.error);
    return result;
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// TRUST CENTER
// ═══════════════════════════════════════════════════════════════════════════

export const trustCenterService = {
  getConfig: () => apiCall('trust-center', 'config'),
  getPublicPortal: (slug: string) => apiCall('trust-center', 'public', { id: slug as any }),
  getDocuments: () => apiCall('trust-center', 'documents'),
  getAccessRequests: () => apiCall('trust-center', 'access-requests'),
  getQuestionnaires: () => apiCall('trust-center', 'questionnaires'),
  getSubProcessors: () => apiCall('trust-center', 'sub-processors'),
  getAnalytics: () => apiCall('trust-center', 'analytics'),
  updateConfig: (data: any) => apiCall('trust-center', 'update-config', { method: 'PUT', body: data }),
  addDocument: (data: any) => apiCall('trust-center', 'add-document', { method: 'POST', body: data }),
  approveAccessRequest: (id: number) => apiCall('trust-center', 'approve-request', { method: 'POST', id }),
  denyAccessRequest: (id: number) => apiCall('trust-center', 'deny-request', { method: 'POST', id }),
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPLIANCE CHATBOT
// ═══════════════════════════════════════════════════════════════════════════

export const chatbotService = {
  getModes: () => apiCall('chatbot', 'modes'),
  startConversation: (userId: number, mode?: string, context?: any) =>
    apiCall('chatbot', 'start', { method: 'POST', body: { user_id: userId, mode, context } }),
  sendMessage: (params: { conversationId?: string | null; message: string; mode?: string; userId?: number }) =>
    apiCall('chatbot', 'message', { method: 'POST', body: { conversation_id: params.conversationId, message: params.message, mode: params.mode, user_id: params.userId || 1 } }),
  getConversations: (userId: number) => apiCall('chatbot', 'conversations', { id: userId }),
  searchKnowledge: (keywords: string[]) =>
    apiCall('chatbot', 'search', { method: 'POST', body: { keywords } }),
  startGuidedAssessment: (userId: number, assessmentType: string, params?: any) =>
    apiCall('chatbot', 'guided-assessment', { method: 'POST', body: { user_id: userId, assessment_type: assessmentType, params } }),
  reportIssue: (data: { subject: string; description: string; priority: 'low' | 'medium' | 'high' | 'critical'; category: string }) =>
    apiCall('chatbot', 'report-issue', { method: 'POST', body: data }),
};

// ═══════════════════════════════════════════════════════════════════════════
// EXECUTIVE DASHBOARD
// ═══════════════════════════════════════════════════════════════════════════

export const executiveService = {
  getSummary: () => apiCall('executive', 'summary'),
  getTrends: (days?: number) => apiCall('executive', 'trends', { id: days as any }),
  getReports: () => apiCall('executive', 'reports'),
  getBenchmark: (industry?: string) => apiCall('executive', 'benchmark', { id: industry as any }),
  generateReport: (reportType: string, options?: any) =>
    apiCall('executive', 'generate-report', { method: 'POST', body: { report_type: reportType, options } }),
  getDashboardConfig: (userId: number) => apiCall('executive', 'dashboard-config', { id: userId }),
  saveDashboardConfig: (userId: number, widgets: any) =>
    apiCall('executive', 'save-dashboard', { method: 'POST', body: { user_id: userId, widgets } }),
};

// Export all services
export const enterpriseService = {
  vendorRisk: vendorRiskService,
  training: trainingService,
  automation: automationService,
  cloud: cloudService,
  saas: saasService,
  endpoints: endpointService,
  pentest: pentestService,
  trustCenter: trustCenterService,
  chatbot: chatbotService,
  executive: executiveService,
};

export default enterpriseService;
